import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import { updateTimeOffRequest } from '../../services/timeOffService';
import { decideTimesheetWeeks } from '../../services/timesheetService';
import { usePermissions } from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import useMinimumLoader from '../../hooks/useMinimumLoader';
import useBatchRunner from '../../hooks/useBatchRunner';
import ui from './DashboardUI.module.css';
import styles from './home/Home.module.css';
import ChecklistCard from '../../components/onboarding/ChecklistCard';
import SegmentedControl from '../../components/common/SegmentedControl';
import WeekPager from '../../components/common/WeekPager';
import Toast from '../../components/Toast';
import KpiTiles from './home/KpiTiles';
import NeedsAttention from './home/NeedsAttention';
import TeamCapacity from './home/TeamCapacity';
import ProjectBudgets from './home/ProjectBudgets';
import UpcomingDeadlines from './home/UpcomingDeadlines';
import MyWork from './home/MyWork';
import LeaveModal from './home/LeaveModal';
import { weekRangeLabel } from './home/homeData';
import { mondayOf, toISODate } from '../../utils/weekDates';

const openProjectState = (id) => ({ openProjectDetailModal: id });

// Dismissals live in this browser only (v1): one list per user.
const useDismissed = (userId) => {
  const key = `dashboardDismissed:${userId}`;
  const [keys, setKeys] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) || []; } catch { return []; } });
  const dismiss = (k) => setKeys((prev) => {
    const next = [...prev, k];
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });
  return [keys, dismiss];
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { user } = useAuth();
  const { canViewManagerDashboard, canViewSchedule, canCreateTimesheets, canCreateOwnTimeOff, canApproveTimeOff } = usePermissions();

  const [view, setView] = useState(canViewManagerDashboard ? 'team' : 'mine');
  const [weekStart, setWeekStart] = useState(() => mondayOf(toISODate(new Date())));
  const [manager, setManager] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLeave, setShowLeave] = useState(false);
  const [toast, setToast] = useState(null);
  const [dismissed, dismiss] = useDismissed(user.user_id);
  const runBatch = useBatchRunner(setToast);
  const showLoading = useMinimumLoader(loading);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (view === 'team') {
        const [snapshot, capacity, attention, overview, deadlines] = await Promise.all([
          dashboardService.getExecutiveSnapshot(),
          canViewSchedule ? dashboardService.getCapacity(weekStart) : [],
          dashboardService.getAttentionRequired(),
          dashboardService.getProjectStatusOverview(),
          dashboardService.getUpcomingDeadlines(),
        ]);
        setManager({ snapshot, capacity, attention, overview, deadlines });
      } else {
        setPersonal(await dashboardService.getPersonalDashboard(weekStart));
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [view, weekStart, canViewSchedule]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    if (!routeLocation.state?.openTimeOffModal || !(canCreateOwnTimeOff || canApproveTimeOff)) return;
    setShowLeave(true);
    window.history.replaceState({}, document.title);
  }, [routeLocation.state, canCreateOwnTimeOff, canApproveTimeOff]);

  const openProject = (id) => navigate('/dashboard/projects', { state: openProjectState(id) });
  const decideLeave = (item, status) => runBatch(
    () => updateTimeOffRequest(item.target_id, { status }),
    load,
    () => `Leave ${status === 'approved' ? 'approved' : 'declined'}.`,
  );
  const remind = (item) => runBatch(
    () => decideTimesheetWeeks({ action: 'remind', week_start: item.week_start, employee_ids: item.employee_ids }),
    load,
    (res) => `Reminder sent to ${res.reminded} ${res.reminded === 1 ? 'person' : 'people'}.`,
  );
  const openAttention = (item) => {
    if (item.type === 'leave_request') navigate('/dashboard/leave-manager');
    else if (item.type === 'overbooked') navigate(`/dashboard/schedule?open=${encodeURIComponent(item.name)}`);
    else openProject(item.target_id);
  };

  const leaveLabel = canApproveTimeOff ? 'Schedule leave' : 'Request leave';
  const statusMix = (manager?.overview || [])
    .filter((s) => Number(s.project_count) > 0)
    .map((s) => ({ label: s.status_name, value: Number(s.project_count), color: s.status_color || '#6b7280' }));

  if (error) return <div className={ui.pageContainer}><div className={styles.empty}>Error loading dashboard: {error}</div></div>;

  return (
    <div className={ui.pageContainer}>
      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: 'center' }}>
          <h1 className={ui.pageTitle}>Dashboard</h1>
          {view === 'mine' && <WeekPager weekStart={weekStart} label={weekRangeLabel(weekStart)} onChange={setWeekStart} />}
        </div>
        <div className={ui.pageHeaderRight}>
          {canViewManagerDashboard && (
            <SegmentedControl ariaLabel="Team or My work" value={view} onChange={setView} options={[{ value: 'team', label: 'Team' }, { value: 'mine', label: 'My work' }]} />
          )}
          {canCreateTimesheets && (
            <button type="button" className={ui.cancelButton} onClick={() => navigate('/dashboard/timesheets', { state: { openLogTime: true } })}>Log time</button>
          )}
          {(canCreateOwnTimeOff || canApproveTimeOff) && (
            <button type="button" className={ui.addButton} onClick={() => setShowLeave(true)}>{leaveLabel}</button>
          )}
        </div>
      </div>

      <div className={styles.page}>
        <ChecklistCard />
        {showLoading && <div className={styles.empty}>Loading…</div>}

        {!showLoading && view === 'team' && manager && (
          <div className={ui.fadeInContent} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <KpiTiles snapshot={manager.snapshot} statusMix={statusMix} />
            <div className={styles.twoCol}>
              <NeedsAttention
                items={manager.attention.filter((i) => !dismissed.includes(i.key))}
                canApprove={canApproveTimeOff}
                onDecide={decideLeave}
                onOpen={openAttention}
                onRemind={remind}
                onDismiss={dismiss}
              />
              <UpcomingDeadlines deadlines={manager.deadlines} onOpenProject={openProject} />
            </div>
            {canViewSchedule && (
              <TeamCapacity people={manager.capacity} weekStart={weekStart} onWeekChange={setWeekStart} onOpenPerson={(p) => navigate(`/dashboard/schedule?open=${encodeURIComponent(p.name)}`)} />
            )}
            <ProjectBudgets overview={manager.overview} onOpenProject={openProject} />
          </div>
        )}

        {!showLoading && view === 'mine' && personal && (
          <div className={ui.fadeInContent} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <MyWork
              data={personal}
              weekStart={weekStart}
              canLog={canCreateTimesheets}
              runBatch={runBatch}
              reload={load}
              onRequestLeave={canCreateOwnTimeOff || canApproveTimeOff ? () => setShowLeave(true) : null}
              onOpenProject={openProject}
            />
          </div>
        )}
      </div>

      {showLeave && <LeaveModal onClose={() => setShowLeave(false)} onSaved={() => { setShowLeave(false); load(); }} />}
      {toast && <Toast message={toast.message} type={toast.type} action={toast.action} onClose={() => setToast(null)} />}
    </div>
  );
}
